import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Calendar,
  Filter,
  RefreshCw,
  Clock,
  Phone,
  DollarSign,
  Users,
  Globe,
  Activity,
  PieChart
} from 'lucide-react';
import { dashboardAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import clsx from 'clsx';

const AnalyticsPage = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');
  const [selectedMetric, setSelectedMetric] = useState('calls');

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getAnalytics({
        timeRange,
        includeCharts: true
      });
      setAnalyticsData(response.data.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) {return '0';}
    if (num >= 1000000) {return `${(num / 1000000).toFixed(1)}M`;}
    if (num >= 1000) {return `${(num / 1000).toFixed(1)}K`;}
    return num.toString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const getTimeRangeLabel = (range) => {
    switch (range) {
      case '24h': return 'Last 24 Hours';
      case '7d': return 'Last 7 Days';
      case '30d': return 'Last 30 Days';
      case '90d': return 'Last 90 Days';
      default: return 'Custom Range';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading analytics..." />
      </div>
    );
  }

  const {
    overview = {},
    callMetrics = {},
    revenueMetrics = {},
    userMetrics = {},
    geographicData = {},
    performanceMetrics = {},
    trends = {},
    languageBreakdown = {}
  } = analyticsData || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Comprehensive insights and performance metrics
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>

          <button
            onClick={loadAnalytics}
            className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>

        </div>
      </div>

      {/* Key Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Calls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Calls</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(callMetrics.totalCalls)}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <Phone className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            {trends.callsChange >= 0 ? (
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
            )}
            <span className={clsx(
              'text-sm font-medium',
              trends.callsChange >= 0 ? 'text-green-600' : 'text-red-600'
            )}>
              {formatPercentage(Math.abs(trends.callsChange))}
            </span>
            <span className="text-sm text-gray-500 ml-1">vs previous period</span>
          </div>
        </div>

        {/* Revenue */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Revenue</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(revenueMetrics.totalRevenue)}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            {trends.revenueChange >= 0 ? (
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
            )}
            <span className={clsx(
              'text-sm font-medium',
              trends.revenueChange >= 0 ? 'text-green-600' : 'text-red-600'
            )}>
              {formatPercentage(Math.abs(trends.revenueChange))}
            </span>
            <span className="text-sm text-gray-500 ml-1">vs previous period</span>
          </div>
        </div>

        {/* Active Users */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Users</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(userMetrics.activeUsers)}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            {trends.usersChange >= 0 ? (
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
            )}
            <span className={clsx(
              'text-sm font-medium',
              trends.usersChange >= 0 ? 'text-green-600' : 'text-red-600'
            )}>
              {formatPercentage(Math.abs(trends.usersChange))}
            </span>
            <span className="text-sm text-gray-500 ml-1">vs previous period</span>
          </div>
        </div>

        {/* Success Rate */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Success Rate</p>
              <p className="text-2xl font-bold text-gray-900">{formatPercentage(performanceMetrics.successRate)}</p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg">
              <Activity className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <BarChart3 className="w-4 h-4 text-gray-400 mr-1" />
            <span className="text-sm text-gray-500">
              {formatNumber(callMetrics.completedCalls)} completed
            </span>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Call Volume Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Call Volume Trend</h3>
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value="calls">Calls</option>
              <option value="duration">Duration</option>
              <option value="revenue">Revenue</option>
            </select>
          </div>

          {/* Placeholder for chart - in a real app, use Chart.js or similar */}
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Chart visualization would appear here</p>
              <p className="text-xs text-gray-400 mt-1">
                Showing {getTimeRangeLabel(timeRange).toLowerCase()} data
              </p>
            </div>
          </div>
        </div>

        {/* Language Distribution */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Language Distribution</h3>

          <div className="space-y-4">
            {Object.entries(languageBreakdown).map(([language, data]) => (
              <div key={language} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                  <span className="text-sm font-medium text-gray-900 capitalize">{language}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">{formatNumber(data.calls)} calls</span>
                  <span className="text-xs text-gray-500">
                    ({formatPercentage(data.percentage)})
                  </span>
                </div>
              </div>
            ))}
          </div>

          {Object.keys(languageBreakdown).length === 0 && (
            <div className="h-32 flex items-center justify-center">
              <div className="text-center">
                <Globe className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">No language data available</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detailed Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Call Performance */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Call Performance</h3>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Average Duration</span>
              <span className="text-sm font-medium text-gray-900">
                {Math.floor((performanceMetrics.avgDuration || 0) / 60)}m {(performanceMetrics.avgDuration || 0) % 60}s
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Answer Rate</span>
              <span className="text-sm font-medium text-gray-900">
                {formatPercentage(performanceMetrics.answerRate)}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Drop Rate</span>
              <span className="text-sm font-medium text-gray-900">
                {formatPercentage(performanceMetrics.dropRate)}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Avg Wait Time</span>
              <span className="text-sm font-medium text-gray-900">
                {performanceMetrics.avgWaitTime || 0}s
              </span>
            </div>
          </div>
        </div>

        {/* Revenue Breakdown */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Breakdown</h3>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Call Charges</span>
              <span className="text-sm font-medium text-gray-900">
                {formatCurrency(revenueMetrics.callCharges)}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Subscription Fees</span>
              <span className="text-sm font-medium text-gray-900">
                {formatCurrency(revenueMetrics.subscriptionFees)}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Usage Overage</span>
              <span className="text-sm font-medium text-gray-900">
                {formatCurrency(revenueMetrics.usageOverage)}
              </span>
            </div>

            <div className="border-t pt-3 mt-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-900">Total Revenue</span>
                <span className="text-sm font-bold text-gray-900">
                  {formatCurrency(revenueMetrics.totalRevenue)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Geographic Data */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Regions</h3>

          <div className="space-y-3">
            {Object.entries(geographicData).slice(0, 5).map(([region, data]) => (
              <div key={region} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-sm text-gray-900">{region}</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {formatNumber(data.calls)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatPercentage(data.percentage)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {Object.keys(geographicData).length === 0 && (
            <div className="h-32 flex items-center justify-center">
              <div className="text-center">
                <Globe className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">No geographic data available</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Insights and Recommendations */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Insights</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">Performance Highlights</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center">
                <TrendingUp className="w-4 h-4 text-green-500 mr-2" />
                Call volume increased by {formatPercentage(Math.abs(trends.callsChange))} this period
              </li>
              <li className="flex items-center">
                <Clock className="w-4 h-4 text-blue-500 mr-2" />
                Average call duration: {Math.floor((performanceMetrics.avgDuration || 0) / 60)} minutes
              </li>
              <li className="flex items-center">
                <Activity className="w-4 h-4 text-purple-500 mr-2" />
                Success rate maintained at {formatPercentage(performanceMetrics.successRate)}
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">Recommendations</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Consider scaling infrastructure for peak usage hours</li>
              <li>• Monitor drop rates in underperforming regions</li>
              <li>• Optimize language detection for better user experience</li>
              <li>• Review pricing strategy for usage-based billing</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;